import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import { type PlantTypeText } from '@common/types/plant';
import {
  type AvailableQuarry,
  type UserQuarry,
} from '@common/types/quarryType';
import { type ResourceTypeText } from '@common/types/resource';
import { CardMaterial } from '@components/Materials/CardMaterial/CardMaterial';
import cn from 'classnames';

import { OpenPlantsBoost } from '@/components/OpenPlantsBoost/OpenPlantsBoost';
import { H3 } from '@/components/Typography/Typography';

import CardResourceLi from './CardResourceLi';

interface CardsResourcesProps {
  kind: 'resource';
  buildsObject?: Partial<Record<ResourceTypeText, UserQuarry>>;
  nextToBuild?: AvailableQuarry;
  list: ResourceTypeText[];
}

interface CardsMaterialsProps {
  kind: 'material';
  buildsObject?: Partial<Record<PlantTypeText, UserFactory>> | undefined;
  nextToBuild?: UserFactoryNextToBuild;
  list: PlantTypeText[];
}

const CardsResources: FC<CardsResourcesProps | CardsMaterialsProps> = memo(
  ({ buildsObject, nextToBuild, list, kind }) => {
    const { t } = useTranslation();

    return (
      <>
        <div className="w-full flex justify-between items-center">
          <H3 weight="bold" className="isolate" style={{ fontSize: '1.25rem' }}>
            {t(kind === 'resource' ? 'Select a resource' : 'Select material')}
          </H3>
          <OpenPlantsBoost kind={kind} />
        </div>
        <div
          className={cn(
            'grid grid-cols-3 gap-1',
            kind === 'resource'
              ? 'grid-rows-[6rem_6rem]'
              : 'grid-rows-[6rem_6rem_6rem]',
          )}
        >
          {list.map((key, index) => {
            return kind === 'resource' ? (
              <CardResourceLi
                key={index}
                name={key as ResourceTypeText}
                data={
                  buildsObject && buildsObject[key as keyof typeof buildsObject]
                }
                nextToBuild={nextToBuild}
              />
            ) : (
              <CardMaterial
                key={index}
                name={key as PlantTypeText}
                data={
                  buildsObject && buildsObject[key as keyof typeof buildsObject]
                }
                nextToBuild={nextToBuild}
              />
            );
          })}
        </div>
      </>
    );
  },
);

export default CardsResources;
